import { isEmpty } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NoItemResult from '../../../components/results/NoItemResult';
import GroupedColumnChart from './Charts/GroupedColumnChart';
import { useDashboardContext } from './DashboardContext';
import useApplicationSubmittedData from './data/useApplicationSubmittedData';
import { FullScreenWrapper } from './shared/FullScreenWrapper';
import { type ChartDataProps, type Datum } from './shared/typings';
import useLegendSelectedConfig from './shared/useLegendSelectedConfig';
import useSeriesColors from './shared/useSeriesColors';
import useSeriesFields from './shared/useSeriesField';

export const ApplicationCharttViewID = 'applicationChart';

const ApplicationChartContent = ({ data, t, noData }: ChartDataProps) => {
    const { currentMonth, lastMonth } = useSeriesFields();
    const legendSelected = useLegendSelectedConfig({ currentMonth, lastMonth });
    const color = useSeriesColors();

    if (noData) {
        return <NoItemResult subTitle={t('dashboard:noData.message')} />;
    }

    return (
        <GroupedColumnChart
            {...t('dashboard:applicationChart.chart', { returnObjects: true })}
            color={color}
            data={data}
            visible={legendSelected}
            xField="day"
            yField="noOfSubmitted"
        />
    );
};

const ApplicationChart = () => {
    const { t } = useTranslation(['dashboard']);
    const [data, setData] = useState<Datum | null>(null);
    const { isLoading, error, generateData } = useApplicationSubmittedData();

    useEffect(() => {
        generateData(result => setData(result));
    }, [generateData, setData]);

    const noData = useMemo(() => (data ? Object.values(data).every(isEmpty) : true), [data]);

    const { setVisible } = useDashboardContext();

    useEffect(() => {
        setVisible({ [ApplicationCharttViewID]: !error });
    }, [error, setVisible]);

    return (
        <FullScreenWrapper
            disabledFullScreen={noData}
            height={585}
            isLoading={isLoading}
            title={t('dashboard:applicationChart.title')}
            tooltip={t('dashboard:applicationChart.tooltip')}
        >
            <ApplicationChartContent data={data} noData={noData} t={t} />
        </FullScreenWrapper>
    );
};

export default ApplicationChart;
